import { FC, ReactNode, useState, Suspense, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useQueryLoader, PreloadedQuery, usePreloadedQuery } from 'react-relay';

//components
import { FormattedPrice } from 'dibs-buyer-price';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { shippingMethodDescriptions } from '../messages/pdpShippingInfoMessages';
import { PdpSharedShippingInfoLink } from './PdpSharedShippingInfoLink';
import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';
import ShippingIcon from 'dibs-icons/exports/Shipping';
import { PdpSharedShipsFrom } from './PdpSharedShipsFrom';

import { FREE_SHIPPING_AND_COST } from 'dibs-search-product-tile/exports/constants';
import { useShippingPrice } from 'dibs-search-product-tile/exports/useShippingPrice';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import PdpSharedShippingInfoLoading from './PdpSharedShippingInfoLoading';

import styles from './PdpSharedShippingInfo.scss';

import {
    PdpSharedShippingInfoQuery as PdpSharedShippingInfoQueryType,
    PdpSharedShippingInfoQuery$variables as PdpSharedShippingInfoQueryVariablesType,
    PdpSharedShippingInfoQuery$data as PdpSharedShippingInfoQueryResponseType,
} from './__generated__/PdpSharedShippingInfoQuery.graphql';

const PdpSharedShippingInfoQuery = graphql`
    query PdpSharedShippingInfoQuery(
        $itemId: String!
        $userId: String!
        $fetchRegionalInfo: Boolean = false
        $userZipCode: String = ""
        $userCountryCode: String = ""
        $isTrade: Boolean = false
        $fetchItem: Boolean = false
        $fetchUser: Boolean = false
        $isClient: Boolean = false
        $hidePaidShipping: Boolean = false
    ) {
        viewer {
            ...PdpSharedShippingHolidayDelivery_viewer
            ...PdpSharedShippingInfoLink_viewer
            ...PdpSharedPromoCode_viewer
            showShippingDiscounts: featureFlag(feature: "subsidizedShippingPDP")
            relevantShipping(itemId: $itemId, buyerId: $userId) {
                quoteRequestStatus
                shipmentQuote {
                    ...PdpSharedShippingInfoLink_shipmentQuote
                    handlingTimeDisplay(displayShort: true)
                    subTotalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    buyerServiceMethodDisplay
                    showQuoteAsPending
                    serviceMethod {
                        serviceType
                        serviceMethodType
                    }
                }
                shipment {
                    shippingAddress {
                        displayCityStateCountry
                    }
                }
            }

            ...useShippingPrice_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode) {
                countryName
                zipCode
            }

            item(itemId: $itemId) @include(if: $fetchItem) {
                serviceId
                ...PdpSharedCustomsMessage_item
                ...useShippingPrice_item @arguments(buyerId: $userId, fetchShippingCostInfo: true)
                ...PdpSharedShippingInfoLink_item
                ...PdpSharedShippingInfoWrapper_item
                ...PdpSharedShipsFrom_item @include(if: $hidePaidShipping)
            }

            user(userId: $userId) @include(if: $fetchUser) {
                serviceId
                addressBook {
                    proAddress {
                        displayCountry
                        zipCode
                    }
                    personalAddress {
                        displayCountry
                        zipCode
                    }
                    addressList {
                        displayCountry
                        zipCode
                    }
                }
                ...PdpSharedPromoCode_user
                ...PdpSharedShippingInfoLink_user
            }

            activePromotion(userId: $userId, itemId: $itemId) @include(if: $hidePaidShipping) {
                isShippingPromotion
            }
        }
    }
`;

const SHIPPING_TYPE = {
    PAID: 'paid',
    PAID_WITH_DISCOUNT: 'paid_discounted',
    FREE_SHIPPING: 'free',
    FREE_SHIPPING_WITH_DISCOUNT: 'free_discounted',
};

export const AVAILABLE_NOW = 'available-now';

export const REQUEST_STATUS = {
    REQUEST_QUOTE: 'requestQuote',
    CUSTOM_PROVIDED: 'customQuoteProvided',
    CUSTOM_REQUESTED: 'customQuoteRequested',
};

const getRegionName = ({
    quoteRequestStatus,
    addressBook,
    regionalInfo,
    customQuoteShippingAddress,
}: {
    quoteRequestStatus?: string | null;
    addressBook: NonNullable<
        PdpSharedShippingInfoQueryResponseType['viewer']['user']
    >['addressBook'];
    regionalInfo: PdpSharedShippingInfoQueryResponseType['viewer']['regionalInfo'];
    customQuoteShippingAddress?: string | null;
}): string => {
    const { personalAddress, proAddress, addressList } = addressBook || {};
    const { displayCountry, zipCode } = proAddress || personalAddress || addressList?.[0] || {};
    const { countryName, zipCode: regionalZipCode } = regionalInfo?.[0] || {};

    if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_PROVIDED) {
        return customQuoteShippingAddress || '';
    }

    if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_REQUESTED) {
        return customQuoteShippingAddress || '';
    }

    return `${displayCountry || countryName || ''} ${zipCode || regionalZipCode || ''}`;
};

const getShippingType = ({
    price,
    fullPrice,
    showShippingDiscounts,
}: {
    price: number | null;
    fullPrice: number;
    showShippingDiscounts: boolean | null;
}): string => {
    const isFreeShipping = price === 0 && showShippingDiscounts;
    const isDiscountedShipping =
        (!!price || price === 0) && fullPrice !== 0 && fullPrice > price && showShippingDiscounts;

    if (isFreeShipping && isDiscountedShipping) {
        return SHIPPING_TYPE.FREE_SHIPPING_WITH_DISCOUNT;
    }

    if (isFreeShipping) {
        return SHIPPING_TYPE.FREE_SHIPPING;
    }

    if (isDiscountedShipping) {
        return SHIPPING_TYPE.PAID_WITH_DISCOUNT;
    }

    return SHIPPING_TYPE.PAID;
};

const getShippingTypeMessage = ({
    price,
    fullPrice,
    currency,
    showShippingDiscounts,
}: {
    price: number | null;
    fullPrice: number;
    currency: string;
    showShippingDiscounts: boolean | null;
}): ReactNode => {
    return (
        <FormattedMessage
            id="pdp.PdpSharedShippingInfoComponent.shippingType"
            defaultMessage="{shippingType, select,
                free {<wrapper>Free Shipping:</wrapper>}
                free_discounted {<wrapper>Free Shipping:</wrapper> {discountPrice}}
                paid {{shippingPrice}}
                paid_discounted {{discountPrice} {styledShippingPrice}}
                other {}
            }"
            values={{
                shippingType: getShippingType({
                    price,
                    fullPrice,
                    showShippingDiscounts,
                }),
                discountPrice: (
                    <span className={styles.discountedPrice}>
                        <FormattedPrice currency={currency} value={fullPrice} />
                    </span>
                ),
                shippingPrice: (
                    <span>
                        <FormattedPrice currency={currency} value={price || 0} />
                    </span>
                ),
                styledShippingPrice: (
                    <span className={styles.salePrice}>
                        <FormattedPrice currency={currency} value={price || 0} />
                    </span>
                ),
                wrapper: message => <span className={styles.freeShippingMessage}>{message}</span>,
                Wrapper: message => <div>{message}</div>,
            }}
        />
    );
};

type PdpSharedShippingInfoProps = {
    itemId: string;
    userId: string;
    currency: string;
    leadTime?: string;
    renderTooltip?: boolean;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    title?: ReactNode;
    displayInline?: boolean;
    isTrade?: boolean;
    isClient?: boolean;
    hidePaidShipping?: boolean;
};

type PdpSharedShippingInfoComponentProps = {
    queryReference: PreloadedQuery<PdpSharedShippingInfoQueryType>;
    currency: string;
    leadTime?: string;
    renderTooltip?: boolean;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    title?: ReactNode;
    displayInline?: boolean;
    reloadQuery?: (arg: PdpSharedShippingInfoQueryVariablesType) => void;
    hidePaidShipping?: boolean;
};

export const PdpSharedShippingInfoComponent: FC<PdpSharedShippingInfoComponentProps> = ({
    currency,
    leadTime,
    renderTooltip,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    title,
    displayInline,
    queryReference,
    reloadQuery,
    hidePaidShipping,
}) => {
    const [showSpinner, setShowSpinner] = useState(true);
    const [tooltipHover, setTooltipHover] = useState(false);
    const { viewer } = usePreloadedQuery<PdpSharedShippingInfoQueryType>(
        PdpSharedShippingInfoQuery,
        queryReference
    );
    const {
        showShippingDiscounts = false,
        relevantShipping,
        regionalInfo,
        item,
        user,
        activePromotion,
    } = viewer || {};
    const { serviceId: itemId } = item || {};
    const { shipment, shipmentQuote, quoteRequestStatus } = relevantShipping || {};

    const {
        subTotalAmount,
        buyerServiceMethodDisplay: shippingMethodDisplay,
        handlingTimeDisplay,
        showQuoteAsPending,
    } = shipmentQuote || {};

    const customQuoteShippingAddress = shipment?.shippingAddress?.displayCityStateCountry;

    const price = useShippingPrice({
        viewer,
        item: item || null,
        currency,
        type: FREE_SHIPPING_AND_COST,
    });

    const fullPrice =
        subTotalAmount?.convertedAmountList?.find(p => p?.currency === currency)?.amount || 0;

    const showQuoteDetails = quoteRequestStatus !== 'requestQuote';

    let overviewMessage;
    if (!shipmentQuote || quoteRequestStatus === REQUEST_STATUS.REQUEST_QUOTE) {
        overviewMessage = (
            <FormattedMessage
                id="pdp.PdpSharedBlurbShippingCustomQuote.overviewRequestQuote"
                defaultMessage="Rates vary by destination and complexity."
            />
        );
    } else if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_REQUESTED || showQuoteAsPending) {
        overviewMessage = (
            <span data-tn="quote-requested">
                <FormattedMessage
                    id="pdp.PdpSharedBlurbShippingCustomQuote.overviewPendingDetails"
                    defaultMessage="Quote pending to {customQuoteShippingAddress}."
                    values={{ customQuoteShippingAddress }}
                />
            </span>
        );
    } else {
        const { serviceType, serviceMethodType } = shipmentQuote?.serviceMethod || {};
        const shippingMethodDescription =
            serviceType && serviceMethodType
                ? (shippingMethodDescriptions as Record<
                      string,
                      Record<string, ReactNode | undefined>
                  >)?.[serviceType]?.[serviceMethodType]
                : '';

        overviewMessage = (
            <span data-tn="prequote-overview">
                <FormattedMessage
                    id="pdp.PdpSharedShippingInfo.prequoteOverview"
                    defaultMessage="{shippingType} <tooltip>{method} Shipping</tooltip> to {region}, arrives in {handlingTime}{isAvailable, select,
                        true {.}
                        other {, excluding production time.}
                    }"
                    values={{
                        shippingType: getShippingTypeMessage({
                            price,
                            fullPrice,
                            currency,
                            showShippingDiscounts,
                        }),
                        method: shippingMethodDisplay,
                        region: getRegionName({
                            quoteRequestStatus,
                            customQuoteShippingAddress,
                            regionalInfo: regionalInfo,
                            addressBook: user?.addressBook || null,
                        }),
                        handlingTime: handlingTimeDisplay,
                        isAvailable: leadTime === AVAILABLE_NOW,
                        tooltip: content => (
                            <span
                                className={styles.hoverable}
                                onMouseEnter={() => setTooltipHover(true)}
                                onMouseLeave={() => setTooltipHover(false)}
                            >
                                {content}
                                {renderTooltip && shippingMethodDescription && (
                                    <Tooltip isVisible={tooltipHover} direction="bottom">
                                        {shippingMethodDescription}
                                    </Tooltip>
                                )}
                            </span>
                        ),
                    }}
                />
            </span>
        );
    }

    useEffect(() => {
        if (showSpinner) {
            const timer = setTimeout(() => setShowSpinner(false), 2000);
            return () => clearTimeout(timer);
        }
        return () => {};
    }, [showSpinner]);

    const shippingInfo = (
        <span data-tn="pdp-shared-shipping-info">
            <FormattedMessage
                id="pdp.PdpSharedShippingInfo.infoContainer"
                defaultMessage={`{showTitle, select,
                    true {{title}: {content}}
                    other {{content}}
                }`}
                values={{
                    showTitle: price !== 0 && !!title,
                    title,
                    content: (
                        <>
                            <PdpSharedPromoCode
                                onlyShippingPromos
                                excludeShippingPromos={price === 0}
                                itemId={itemId || ''}
                                viewer={viewer}
                                user={user || null}
                                userId={user?.serviceId || ''}
                                displayInfo={null}
                                currency={currency}
                            />
                            {overviewMessage} {showQuoteDetails && preLinkQuoteDetails}
                            <PdpSharedShippingInfoLink
                                currency={currency}
                                shipmentQuote={shipmentQuote}
                                userId={user?.serviceId || ''}
                                item={item}
                                itemId={itemId || ''}
                                user={user || null}
                                viewer={viewer}
                                showQuoteDetails={showQuoteDetails}
                                quoteRequestStatus={quoteRequestStatus}
                                onSuccessfulPreQuoteRequest={reloadQuery}
                                displayInline={displayInline && showQuoteDetails}
                            />
                            {showQuoteDetails && postLinkQuoteDetails}
                            {!showQuoteDetails && postLinkNoQuoteDetails}
                        </>
                    ),
                }}
            />
        </span>
    );

    if (hidePaidShipping) {
        const shippingType = getShippingType({ price, fullPrice, showShippingDiscounts });

        if (showSpinner) {
            return null;
        }

        //Show shipping quotes only if shipping is free or there is an active shipping promotion for the item
        if (
            shippingType === SHIPPING_TYPE.FREE_SHIPPING ||
            shippingType === SHIPPING_TYPE.FREE_SHIPPING_WITH_DISCOUNT ||
            activePromotion?.isShippingPromotion
        ) {
            return (
                <PdpSharedBlurb IconComponent={ShippingIcon} iconClass={styles.shippingIcon}>
                    {shippingInfo}
                    <PdpSharedShipsFrom item={item} />
                </PdpSharedBlurb>
            );
        }

        return null;
    }

    return showSpinner ? <PdpSharedShippingInfoLoading title={title} /> : shippingInfo;
};

export const PdpSharedShippingInfo: FC<PdpSharedShippingInfoProps> = ({
    isClient,
    itemId,
    userId,
    isTrade,
    currency,
    leadTime,
    renderTooltip,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    title,
    displayInline,
    hidePaidShipping,
}) => {
    const [queryReference, loadQuery] = useQueryLoader<PdpSharedShippingInfoQueryType>(
        PdpSharedShippingInfoQuery
    );

    const reloadQuery = useCallback(
        variables => {
            loadQuery({
                ...variables,
                itemId: itemId || '',
                userId: userId || '',
                isTrade,
                fetchUser: !!userId,
                fetchItem: !!itemId,
                isClient,
                hidePaidShipping,
            });
        },
        [loadQuery, isTrade, isClient, itemId, userId, hidePaidShipping]
    );

    useEffect(() => {
        if (isClient) {
            loadQuery({
                itemId: itemId || '',
                userId: userId || '',
                isTrade,
                fetchUser: !!userId,
                fetchItem: !!itemId,
                isClient,
                hidePaidShipping,
            });
        }
    }, [loadQuery, isClient, itemId, userId, isTrade, hidePaidShipping]);

    return queryReference ? (
        <Suspense
            fallback={hidePaidShipping ? null : <PdpSharedShippingInfoLoading title={title} />}
        >
            <PdpSharedShippingInfoComponent
                queryReference={queryReference}
                renderTooltip={renderTooltip}
                currency={currency}
                leadTime={leadTime || ''}
                title={title}
                displayInline={displayInline}
                preLinkQuoteDetails={preLinkQuoteDetails}
                postLinkQuoteDetails={postLinkQuoteDetails}
                postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                reloadQuery={reloadQuery}
                hidePaidShipping={hidePaidShipping}
            />{' '}
        </Suspense>
    ) : null;
};
